import React, { useState, useEffect, useCallback } from 'react';
import { settingsAccess } from '../utils/settingsAccess';
import {
  Box, Typography, Paper, TextField, Switch, FormControlLabel, Button,
  Grid, Alert, CircularProgress, InputAdornment
} from '@mui/material';
import axios from 'axios';
import { useUser } from '../context/UserContext';

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const DEFAULT_LIMITS = {
  maxOfferMiles: { min: 5, max: 500 },
  liveLocationMaxAgeMinutes: { min: 1, max: 240 }
};

/**
 * Dispatch radius settings (owners only). One number governs how far a
 * breakdown may be from any driver or facility it is offered to — in the
 * automatic cycle, in blast rounds, and in the manual "available drivers"
 * list — plus how fresh a driver's phone GPS must be to count as their
 * current position.
 */
function DispatchRadiusCard() {
  const { currentUser } = useUser();
  const isOwner = settingsAccess(currentUser).dispatchSettings;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [status, setStatus] = useState(null); // { severity, message }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/dispatch-config', { headers: getAuthHeader() });
      setConfig(data.config);
      if (data.limits) setLimits(data.limits);
    } catch (err) {
      setStatus({ severity: 'error', message: err.response?.data?.message || 'Failed to load dispatch radius settings' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner) load();
  }, [isOwner, load]);

  if (!isOwner) return null;

  const setField = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      setStatus(null);
      const { data } = await axios.put('/api/dispatch-config', {
        maxOfferMiles: Number(config.maxOfferMiles),
        liveLocationMaxAgeMinutes: Number(config.liveLocationMaxAgeMinutes),
        allowLiveLocationCandidates: !!config.allowLiveLocationCandidates,
        requireAddressOrLiveLocation: !!config.requireAddressOrLiveLocation
      }, { headers: getAuthHeader() });
      setConfig(data.config);
      if (data.limits) setLimits(data.limits);
      setStatus({ severity: 'success', message: 'Dispatch radius settings saved. They apply to the next offer.' });
    } catch (err) {
      setStatus({ severity: 'error', message: err.response?.data?.message || 'Failed to save dispatch radius settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>Dispatch Radius</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Who may be offered a job, based on how far they are from the breakdown. Applies to every
        automatic offer, every blast round, and the available-drivers list in the dispatch dialog.
      </Typography>

      {status && <Alert severity={status.severity} sx={{ mb: 2 }} onClose={() => setStatus(null)}>{status.message}</Alert>}

      {loading || !config ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={28} /></Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Maximum offer distance"
              value={config.maxOfferMiles ?? ''}
              onChange={(e) => setField('maxOfferMiles', e.target.value)}
              inputProps={{ min: limits.maxOfferMiles.min, max: limits.maxOfferMiles.max, step: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">miles</InputAdornment> }}
              helperText="Jobs are only offered to drivers and facilities within this many miles of the breakdown; blast rounds never exceed it either"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Live location counts for"
              value={config.liveLocationMaxAgeMinutes ?? ''}
              onChange={(e) => setField('liveLocationMaxAgeMinutes', e.target.value)}
              inputProps={{ min: limits.liveLocationMaxAgeMinutes.min, max: limits.liveLocationMaxAgeMinutes.max, step: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">minutes</InputAdornment> }}
              helperText="A driver's phone GPS position is treated as where they are for this long; older positions are ignored and their home address is used instead"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!config.allowLiveLocationCandidates}
                  onChange={(e) => setField('allowLiveLocationCandidates', e.target.checked)}
                />
              }
              label="Offer jobs to providers whose on-duty driver is near the breakdown"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
              When a service provider's on-duty driver has a recent phone location within the offer
              distance, their organization is offered the job even if their facility is far away or
              does not cover the job's ZIP. The nearby driver is notified along with the organization.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!config.requireAddressOrLiveLocation}
                  onChange={(e) => setField('requireAddressOrLiveLocation', e.target.checked)}
                />
              }
              label="Require a known position before offering a job to a driver"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
              On: an Org 1 driver with no geocodable home address and no recent phone location is never
              offered a job automatically. Off: such drivers are still offered jobs, but only after
              every driver whose distance is known.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Dispatch Radius'}
            </Button>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default DispatchRadiusCard;
